import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import AppSelect from 'components/AppSelect';
import type { PromptTemplate, PromptTemplateSaveForm, PromptTemplateCategory } from '../../api';

interface TemplateFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: PromptTemplateSaveForm) => Promise<void>;
  template?: PromptTemplate | null;
  categories: PromptTemplateCategory[];
}

export default function TemplateFormModal({
  open,
  onClose,
  onSubmit,
  template,
  categories,
}: TemplateFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(template?.id);

  useEffect(() => {
    if (open) {
      setTitle(template?.title ?? '');
      setDescription(template?.description ?? '');
      setContent(template?.content ?? '');
      setCategoryIds(template?.categories?.map((c) => c.id) ?? []);
      setError(null);
    }
  }, [open, template]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || !content.trim()) {
      setError('Заполните название и текст промпта');
      return;
    }
    if (categories.length === 0) {
      setError('Сначала создайте категорию в каталоге');
      return;
    }
    if (categoryIds.length === 0) {
      setError('Выберите хотя бы одну категорию');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        content: content.trim(),
        category_ids: categoryIds,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  }, [title, description, content, categoryIds, categories.length, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Редактировать промпт' : 'Создать промпт'}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
      >
        <TextField
          label="Название"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          autoFocus
        />
        <TextField
          label="Описание (необязательно)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
        <TextField
          label="Текст промпта"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
          fullWidth
          multiline
          rows={6}
        />
        <AppSelect<PromptTemplateCategory, true>
          multiple
          fullWidth
          disabled={categories.length === 0}
          options={categories}
          value={categories.filter((c) => categoryIds.includes(c.id))}
          onChange={(_, newValue) =>
            setCategoryIds((newValue as PromptTemplateCategory[]).map((c) => c.id))
          }
          getOptionLabel={(option) => option.title || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="Сначала создайте категорию"
          label="Категории"
          renderTags={(value, getTagProps) =>
            (value as PromptTemplateCategory[]).map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.title}
                  size="small"
                  sx={{ bgcolor: option.color ?? '#eee', color: '#fff' }}
                  {...tagProps}
                />
              );
            })
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box
                component="span"
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 1,
                  bgcolor: (option as PromptTemplateCategory).color ?? '#eee',
                  mr: 1,
                  display: 'inline-block',
                }}
              />
              {(option as PromptTemplateCategory).title}
            </li>
          )}
        />
        {error && <Box sx={{ color: 'error.main', fontSize: 14 }}>{error}</Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : isEdit ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
